import { CmsEvent } from '@/service';
import moment from 'moment';

/**
 * @class @name Content
 * @description Exposes menu store module
 */
export default class Event {
	/**
	 * @method @static @get @name namespaced
	 * @description Is this module namespaced?
	 */
	static get namespaced() {
		return true;
	}

	/**
	 * @method @static @get @name state
	 * @description Expose state as an object, compatible with vue stores
	 */
	static get state() {
		return {
			event: null,
			eventList: [],
			totalEvents: 0
		};
	}

	/**
	 * @method @static @get @name getters
	 * @description Expose getters as an object, compatible with vue stores
	 */
	static get getters() {
		return {
			event: (state) => state.event,
			events: (state) => state.eventList,
			totalEvents: (state) => state.totalEvents
		};
	}

	/**
	 * @method @static @get @name mutations
	 * @description Expose mutations as an object, compatible with vue stores
	 */
	static get mutations() {
		return {
			/**
			 * @name setEventList
			 * @description used to set content on store
			 * @param {Object} state
			 * @param {Object} data
			 */
			setEventList: (state, { data, reset }) => {
				state.eventList = reset ? data.data : [...state.eventList, ...data.data];
				state.totalEvents = data.total;
			},

			/**
			 * @name setEvent
			 * @description used to set content on store
			 * @param {Object} state
			 * @param {Object} data
			 */
			setEvent: (state, { data }) => {
				state.event = data;
			}
		};
	}

	/**
	 * @method @static @get @name actions
	 * @description Expose action as an object, compatible with vue stores
	 */
	static get actions() {
		return {
			/**
			 * @async @name loadEventsList
			 * @param {function} commit
			 * @param {Object} filters - Optional filter parameters
			 */
			async loadEventsList({ commit, rootState }, filters = {}) {
				const domain = rootState.CmsSite.site.name || 'default';
				const showPassedEvents = filters?.period === 0 ? false : true;
				let payload = {
					where: { published: { c: 'IS NOT', v: null } },
					showPassedEvents,
					order:
						filters?.period === 0
							? [
									{ direction: 'desc', property: 'featured' },
									{ direction: 'asc', property: 'start' }
							  ]
							: { direction: 'desc', property: 'start' },
					limit: filters?.limit || 3,
					offset: filters?.offset || 0
				};

				if (filters.category && filters.category !== 'All categories') {
					payload.where['event.category'] = filters.category;
				}

				const today = new moment().format('YYYY-MM-DD');
				payload.where['event.start'] = showPassedEvents
					? { type: 'daterange', c: 'BETWEEN', v: ['0001-01-01', today] }
					: { type: 'daterange', c: 'BETWEEN', v: [today, '9999-12-31'] };

				if (filters.year) {
					payload.where['event.start'] = { type: 'daterange', c: 'BETWEEN', v: [filters.year + '-01-01', filters.year + '-12-31'] };
					payload.limit = 0;
				}

				let { data } = await CmsEvent.list(payload, domain);
				commit('setEventList', { data, reset: filters.reset });
			},

			/**
			 * @async @name getEvent
			 * @param {function} commit
			 * @param {Uuid} id - Event ID
			 */
			async getEvent({ commit, rootState }, id) {
				const domain = rootState.CmsSite.site.name || 'default';
				let { data } = await CmsEvent.get(id, domain);
				commit('setEvent', { data: data?.data ? data.data[0] : data });
			},

			/**
			 * @name clearEvent
			 * @param {function} commit
			 */
			clearEvent({ commit }) {
				commit('setEvent', { data: null });
			}
		};
	}
}
